import { Box, ButtonBase, Stack, Typography } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type { ReactNode } from 'react';

export type RowTone = 'primary' | 'error' | 'warning' | 'info' | 'success';

function toneMain(theme: Theme, tone: RowTone): string {
  return theme.palette[tone].main;
}

function toneSurface(theme: Theme, tone: RowTone): string {
  return theme.palette[tone].light;
}

export interface IconBadgeProps {
  readonly tone: RowTone;
  readonly children: ReactNode;
}

/** Small circular tinted badge holding initials or an icon. */
export function IconBadge({ tone, children }: IconBadgeProps) {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{
        flexShrink: 0,
        width: 28,
        height: 28,
        borderRadius: '50%',
        bgcolor: (theme) => toneSurface(theme, tone),
        color: (theme) => toneMain(theme, tone),
        fontSize: (theme) => theme.typography.caption.fontSize,
        fontWeight: 700,
        '& svg': { width: 16, height: 16 },
      }}
    >
      {children}
    </Stack>
  );
}

export interface DenseRowProps {
  readonly leading?: ReactNode;
  readonly primary: ReactNode;
  readonly secondary?: ReactNode;
  readonly trailing?: ReactNode;
  readonly onClick?: () => void;
  readonly alignTop?: boolean;
}

/**
 * Compact, clickable list row: leading badge + primary/secondary text +
 * trailing node. The building block for the VSO activity / follow-up /
 * attention feeds. Theme-driven only.
 */
export function DenseRow({
  leading,
  primary,
  secondary,
  trailing,
  onClick,
  alignTop = false,
}: DenseRowProps) {
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        width: '100%',
        textAlign: 'left',
        justifyContent: 'flex-start',
        alignItems: alignTop ? 'flex-start' : 'center',
        gap: (theme) => theme.spacing(1.5),
        px: (theme) => theme.spacing(1),
        py: (theme) => theme.spacing(1.25),
        borderRadius: (theme) => `${theme.shape.borderRadius}px`,
        '&:hover': { bgcolor: (theme) => theme.palette.action.hover },
      }}
    >
      {leading}
      <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
        {typeof primary === 'string' ? (
          <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
            {primary}
          </Typography>
        ) : (
          primary
        )}
        {secondary != null &&
          (typeof secondary === 'string' ? (
            <Typography
              variant="caption"
              sx={{ color: (theme) => theme.palette.text.secondary }}
              noWrap
              component="div"
            >
              {secondary}
            </Typography>
          ) : (
            secondary
          ))}
      </Box>
      {trailing != null && <Box sx={{ flexShrink: 0 }}>{trailing}</Box>}
    </ButtonBase>
  );
}
